import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BalcaoVirtualService, { Balcao } from "../../../services/BalcaoVirtualService";
import ConsultModal from "../ContributorUpdateFormModal/ConsultModal";
import ConsultarService from "../../../services/ConsultarService";
import { EmptyList } from "./EmptyList";
import Show from "../../Show";
import Button from "../../Button";
import { isChromeOrSafari } from "../../../helpers/Utils";
import style from "./styles.module.scss";

const keyI18n = "translations.virtualDesk";

interface Props {
  show: boolean;
  onHide?(): void;
}

export default function ModalBalcaoVirtual({ show, onHide }: React.PropsWithChildren<Props>) {

  const { t } = useTranslation();
  const [balcoes, setBalcoes] = useState<Balcao[]>([]);
  const [showModalBalcaoVirtual, setShowModalBalcaoVirtual] = useState(false);
  const [desklink, setdesklink] = useState("")
  const [attendantEmail, setAttendantEmail] = useState("");
  const [balcaoId, setBalcaoId] = useState<number | undefined>();
  const [avaliable, setAvaliable] = useState(show);

  const handleShowBalcaoVirtual = async (link : string, attendant_id : number, id: number) => {
    setdesklink(link);
    setShowModalBalcaoVirtual(true);
    setBalcaoId(id);
    await ConsultarService
    .attendant(attendant_id)
    .then((response) => {
      setAttendantEmail(response.email)
    })
  }

  const handleHideBalcaoVirtual = () => {
    setShowModalBalcaoVirtual(false);
  }

  const getBalcoes = async () => {
    await BalcaoVirtualService
      .balcoes()
      .then((response) => {
        setBalcoes(response.filter((b) => b.status !== 'unavailable'));
      })
  }

  useEffect(() => {
    if (!avaliable) return;

    const refreshTimeout = window.setTimeout(() => {
      getBalcoes();
    }, 5000);

    return () => window.clearTimeout(refreshTimeout);
  }, [avaliable, balcoes])

  useEffect(() => {
    setAvaliable(show)
    if (!show) return;

    getBalcoes();

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [show]);

  if (!show) {
    return null;
  }

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && onHide) {
      onHide();
    }
  };

  const getAttendantInitials = (name?: string) => {
    if (!name) return "-";

    const names = name.trim().split(/\s+/);
    const firstInitial = names[0]?.charAt(0) || "";
    const lastInitial = names.length > 1
      ? names[names.length - 1].charAt(0)
      : "";

    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  return (
    <div
      id="modalBalcao"
      className={`modal fade show ${style.modalRoot}`}
      tabIndex={-1}
      role="dialog"
      style={{ display: "block" }}
      onClick={handleBackdropClick}
    >
      { !showModalBalcaoVirtual || !balcaoId ?
        <div className={`modal-dialog ${style.modalDialog}`} role="document">
          <div className={style.modalContent}>
            <div className={style.modalHeader}>
              <h2 className={`pgm-title-large ${style.title}`}>
                {t(`${keyI18n}.title`)}
              </h2>
              <Button
                variant="text"
                size="extra-small"
                iconButton
                type="button"
                className={style.modalCloseButton}
                aria-label={t(`${keyI18n}.aria.close`)}
                onClick={onHide}
                icon={<span className="material-symbols-sharp">close</span>}
              />
            </div>
            <div className={style.modalBody}>
              <div className={style.instructions}>
                <p className={`pgm-title-small ${style.instructionTitle}`}>
                  {t(`${keyI18n}.instructions.title`)}
                </p>
                <ul className={style.instructionList}>
                  <li className="pgm-body-large">
                    {t(`${keyI18n}.instructions.identityDocument`)}
                  </li>
                  <li className="pgm-body-large">
                    {t(`${keyI18n}.instructions.proofOfAddress`)}
                  </li>
                </ul>
              </div>

              <div className={`${style.browserNotice} pgm-body-small`} role="status">
                <span className={`material-symbols-sharp ${style.noticeIcon}`} aria-hidden="true">info</span>
                <span className={style.noticeText}>
                  {t(`${keyI18n}.browserNotice`)}
                </span>
              </div>

              <Show if={isChromeOrSafari()}>
                <div className={style.attendantsList}>
                  {balcoes?.map((guiche) => {
                    const isAvailable = guiche.status === "available";
                    const deskName = guiche.name || t(
                      `${keyI18n}.attendants.deskFallback`,
                      { number: guiche.id },
                    );

                    return (
                      <div key={guiche.id} className={style.attendantItem}>
                        <div className={style.attendantAvatar} aria-hidden="true">
                          {getAttendantInitials(guiche.attendant_name)}
                        </div>

                        <div className={style.attendantInformation}>
                          <span className={`${style.statusBadge} ${isAvailable ? style.statusAvailable : style.statusBusy} pgm-label-small`}>
                            <span className={style.statusDot} aria-hidden="true" />
                            {t(`${keyI18n}.attendants.status.${isAvailable ? "available" : "busy"}`)}
                          </span>
                          <p className={`pgm-body-large ${style.attendantName}`}>
                            {deskName}
                            <span aria-hidden="true"> · </span>
                            {guiche.attendant_name || t(`${keyI18n}.attendants.attendantFallback`)}
                          </p>
                        </div>

                        <Button
                          variant="tonal"
                          size="small"
                          type="button"
                          className={`${isAvailable ? style.callButton : style.busyButton} pgm-title-medium`}
                          disabled={!isAvailable}
                          onClick={() => handleShowBalcaoVirtual(
                            guiche.meet_link,
                            guiche.attendant_id,
                            guiche.id,
                          )}
                          icon={isAvailable
                            ? <span className={`material-symbols-sharp ${style.callIcon}`}>call</span>
                            : undefined}
                        >
                          {t(`${keyI18n}.attendants.actions.${isAvailable ? "call" : "busy"}`)}
                        </Button>
                      </div>
                    );
                  })}
                  {balcoes.length === 0 && <EmptyList />}
                </div>
              </Show>
            </div>
          </div>
        </div> :
        <div className={`${style.contributorConsultModal} mx-auto`}>
          <div className="m-3">
            <ConsultModal
              onHide={handleHideBalcaoVirtual}
              desklink={desklink}
              attendant={attendantEmail}
              balcaoId={balcaoId}
              stopReload={() => setAvaliable(false)}
            />
          </div>
        </div>
      }
    </div>
  );
}
